<template>
  <div class="files-pane">
    <div
      v-if="isLoading"
      class="files-pane__state"
    >
      <b-spinner />
    </div>

    <div
      v-else-if="!items.length && isFiltered"
      class="files-pane__state"
    >
      No Image Files
    </div>

    <button
      v-else-if="!items.length"
      type="button"
      class="files-pane__empty"
      :disabled="!canBrowse"
      @click="$emit('browse')"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="lucide lucide-upload h-8 w-8 opacity-50"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line
          x1="12"
          x2="12"
          y1="3"
          y2="15"
        />
      </svg>
      <span class="files-pane__empty-title">{{ emptyTitle }}</span>
      <span class="files-pane__empty-hint">{{ emptyHint }}</span>
    </button>

    <!-- Two grids, not one: a file may never share a row with a folder, they are different heights. -->
    <template v-else>
      <ItemGrid
        v-for="group in itemGroups"
        :key="group.key"
        class="files-pane__grid"
        :class="group.class"
        :items="group.items"
        :is-selected="isSelected"
        :columns="GRID_COLUMNS"
        :columns-md="GRID_COLUMNS_MD"
        :item-props="{ showCheckbox: true, uploadOnDrop: true, actions }"
        :item-props-for="itemPropsFor"
        @item-click="onItemClick"
        @item-dblclick="onItemDoubleClick"
        @item-count-change="changeCount"
        @item-action="onItemAction"
        @item-drop="onItemDrop"
        @open-preview="(item) => $emit('open-preview', item)"
      />
    </template>

    <ItemPagination
      v-if="lastPage > 1"
      :value="currentPage"
      :total-rows="totalRows"
      :per-page="pageSize"
      @change="fetchItems"
    />
  </div>
</template>

<script>
import ItemGrid from '@frontend/components/modules/file-browser/browser/ItemGrid.vue';
import ItemPagination from '@frontend/components/modules/file-browser/browser/ItemPagination.vue';
import { GRID_COLUMNS, GRID_COLUMNS_MD, GRID_PAGE_SIZE } from '@frontend/constants/select-image-grid';
import { IMAGE_TYPES_QUERY } from '@frontend/constants/file-image-types';
import { isPreviewableImage } from '@frontend/components/modules/file-browser/helpers.js';

const isFolder = (item) => item.type === 'folder';
// The API prepends this cell on every page inside a folder; it is filtered out on fetch.
const isGoBackCell = (item) => item.name === '...';

// Folder-only because `is_favorite` is only ever set for folders (UserFileService).
const FILE_ACTIONS = [
  {
    name: 'toggle-favorite',
    placement: 'corner',
    icon: 'bookmark',
    activeIcon: 'bookmark-fill',
    isVisible: isFolder,
    isActive: (item) => Boolean(item.is_favorite),
    title: (item) => (item.is_favorite ? 'Remove from favorites' : 'Add to favorites'),
  },
  {
    name: 'rename',
    icon: 'pencil',
    title: 'Rename',
    isVisible: isFolder,
  },
  {
    name: 'delete',
    icon: 'trash',
    variant: 'danger',
    title: 'Delete',
  },
];


export default {
  name: 'FilesPane',
  components: {
    ItemGrid,
    ItemPagination,
  },
  data() {
    return {
      items: [],
      folderId: 0,
      folderName: '',
      canBrowse: false,
      currentPage: 1,
      lastPage: 0,
      totalRows: 0,
      isLoading: false,
      isNavigating: false,
      lastFetchKey: null,
      selectedItems: [],
      counts: {},
      allowCounter: false,
      params: {
        searchKey: '',
        sortField: '',
        sortDirection: '',
        favorites: null,
      },
      actions: FILE_ACTIONS,
      pageSize: GRID_PAGE_SIZE,
      GRID_COLUMNS,
      GRID_COLUMNS_MD,
    };
  },
  computed: {
    insideFolder() {
      return Boolean(this.folderId);
    },

    isFiltered() {
      return Boolean(this.params.searchKey || this.params.favorites);
    },

    emptyTitle() {
      return this.folderName ? `"${this.folderName}" is empty` : 'No image files here yet';
    },

    emptyHint() {
      return this.canBrowse ? 'Drag files here, or click to browse' : 'Open a folder to upload files';
    },

    itemGroups() {
      return [
        { key: 'folders', class: 'folder-items', items: this.items.filter(isFolder) },
        { key: 'files', class: '', items: this.items.filter((item) => !isFolder(item)) },
      ].filter((group) => group.items.length);
    },
  },
  methods: {
    // The submit loop turns this key straight into `/share?file=<key>`.
    keyOf(item) {
      return item?.original_path || item?.path || '';
    },

    isSelected(item) {
      const key = this.keyOf(item);

      return this.selectedItems.some((candidate) => this.keyOf(candidate) === key);
    },

    totalCount() {
      return this.totalRows;
    },

    itemPropsFor(item) {
      return {
        showMagnifier: isPreviewableImage(item),
        showCheckbox: !isFolder(item),
        showCounter: this.allowCounter && !isFolder(item) && this.isSelected(item),
        count: this.counts[this.keyOf(item)] || 1,
      };
    },

    onItemAction({ name, item }) {
      this.$emit(name === 'delete' ? 'delete-item' : name, item);
    },

    // The host re-pushes the same filters several times per open, so refetch only on a real change.
    setParams(next) {
      this.applyParams(next);

      if (this.lastFetchKey === this.fetchKey()) {
        return;
      }

      this.fetchItems(1);
    },

    fetchKey() {
      return JSON.stringify([this.folderId, this.params]);
    },

    // For callers that pick their own page (`update_file_images`) and only need the filters current.
    applyParams(next) {
      this.params = { ...this.params, ...next };
    },

    setMultiple(value) {
      this.allowCounter = Boolean(value);

      if (!this.allowCounter) {
        this.counts = {};
      }
    },

    getFolderId() {
      return this.folderId;
    },

    getCurrentPage() {
      return this.currentPage;
    },

    // Called when the modal closes: without it a reopen with identical filters matches the cached
    // key and shows a listing that other parts of the app may have changed in the meantime.
    invalidate() {
      this.lastFetchKey = null;
    },

    // Called right before the host activates the tab; that activation issues the single fetch.
    setFolder(folderId = 0) {
      this.folderId = folderId;
    },

    setFolderName(name = '') {
      this.folderName = name || '';
    },

    setCanBrowse(value = true) {
      this.canBrowse = Boolean(value);
    },

    reload(page = 1, type = 'folder', folderId = 0, data = null) {
      this.folderId = type === 'folder_content' && folderId ? folderId : 0;

      return this.fetchItems(page, data);
    },

    async fetchItems(page = 1, data = null) {
      const endpoint = this.insideFolder ? '/file/folder_content' : '/file/folder_list';
      const query = {
        searchKey: this.params.searchKey || '',
        pageSize: this.pageSize,
        pageNumber: page,
        sort_field: this.params.sortField || '',
        sort: this.params.sortDirection || '',
        favorites: this.params.favorites || null,
      };

      if (this.insideFolder) {
        query.folderId = this.folderId;
        query.type = IMAGE_TYPES_QUERY;
      }

      if (data?.file_path) {
        // The server matches the stored name, which is always .jpg even for a .jpeg upload.
        query.file_path = data.file_path.replace(/\.jpeg$/i, '.jpg');
      }

      this.isLoading = true;
      // Claimed before the await: activations race the first response and would each refetch.
      this.lastFetchKey = this.fetchKey();

      try {
        const response = await axios.get(endpoint, { params: query });

        // The toolbar breadcrumb covers navigation, and `totalCount` never counted this cell.
        this.items = (response.data.items || []).filter((item) => !isGoBackCell(item));
        this.currentPage = response.data.current_page || page;
        this.lastPage = response.data.last_page || 0;
        this.totalRows = response.data.totalCount || 0;

        if (response.data.current_file) {
          this.applyCurrentFile(response.data.current_file);
        }
      } catch (error) {
        console.error('Error fetching files:', error);
      } finally {
        this.isLoading = false;
      }
    },

    applyCurrentFile(currentFile) {
      const item = this.items.find((candidate) => this.keyOf(candidate) === currentFile.path
        || candidate.path === currentFile.path);

      if (item) {
        this.select(item, { exclusive: true });
      }
    },

    selectByPath(path) {
      const item = this.items.find((candidate) => this.keyOf(candidate) === path || candidate.path === path);

      if (item) {
        this.select(item, { exclusive: true });
      }

      return Boolean(item);
    },

    onItemClick(item) {
      if (item.type === 'folder') {
        this.navigateTo(item);

        return;
      }

      if (item.type === 'psd') {
        this.$emit('open-psd', item);

        return;
      }

      this.toggle(item);
    },

    // `ItemGrid` binds `@drop.stop`, so this never reaches the modal handler that would prevent it —
    // without this call the browser navigates to the dropped image.
    onItemDrop({ event, item }) {
      event.preventDefault();

      const files = event.dataTransfer?.files;

      if (!files?.length) {
        return;
      }

      // A refused navigation leaves the pane on the previous folder, which is where the upload
      // would then land.
      if (isFolder(item) && !this.navigateTo(item)) {
        return;
      }

      this.$emit('files-drop', files);
    },

    onItemDoubleClick(item) {
      if (item.type === 'folder' || item.type === 'psd') {
        return;
      }

      if (!this.isSelected(item)) {
        this.toggle(item);
      }

      this.$emit('submit-selection');
    },

    navigateTo(item) {
      if (this.isNavigating) {
        return false;
      }

      this.isNavigating = true;
      this.folderId = item.id;
      this.folderName = item.name || '';
      this.$emit('navigate', { id: item.id, name: item.name });

      this.fetchItems(1).finally(() => {
        this.isNavigating = false;
      });

      return true;
    },

    toggle(item) {
      this.isSelected(item) ? this.deselect(item) : this.select(item);
    },

    select(item, { exclusive = false } = {}) {
      const key = this.keyOf(item);

      if (!key) {
        return;
      }

      if (exclusive || (!this.allowCounter && !window.fieldsReplaceAllImage)) {
        this.selectedItems = [item];
        this.counts = {};
      } else {
        this.selectedItems = [...this.selectedItems, item];
      }

      this.emitSelection();
    },

    deselect(item) {
      const key = this.keyOf(item);

      this.selectedItems = this.selectedItems.filter((candidate) => this.keyOf(candidate) !== key);
      this.$delete(this.counts, key);
      this.emitSelection();
    },

    changeCount({ item, delta }) {
      const key = this.keyOf(item);
      const next = (this.counts[key] || 1) + delta;

      if (next <= 0) {
        this.deselect(item);

        return;
      }

      this.$set(this.counts, key, next);
      this.emitSelection();
    },

    // The matching items usually aren't loaded yet, so stand-ins carry the key until they arrive.
    preselect(paths) {
      this.selectedItems = paths.map((path) => ({
        id: path,
        original_path: path,
        name: String(path).split('/').pop(),
      }));
      this.counts = {};
      this.emitSelection();
    },

    clearSelection() {
      this.selectedItems = [];
      this.counts = {};
      this.emitSelection();
    },

    emitSelection() {
      this.$emit('onSelectedItems', this.selectedItems.map((item) => ({
        ...item,
        count: this.counts[this.keyOf(item)] || 1,
      })));
    },
  },
};
</script>

<style scoped>
.files-pane__state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #73808c;
}

.files-pane__empty {
  --files-pane-accent: hsl(162, 73%, 35%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 240px;
  padding: 24px;
  border: 2px dashed #cbd5e1;
  border-radius: 10px;
  background-color: transparent;
  color: #94a3b8;
}

.files-pane__empty:not(:disabled) {
  cursor: pointer;
}

.files-pane__empty:not(:disabled):hover {
  border-color: var(--files-pane-accent);
  background-color: rgba(22, 163, 139, 0.03);
  color: var(--files-pane-accent);
}

.files-pane__empty:not(:disabled):hover .files-pane__empty-title,
.files-pane__empty:not(:disabled):hover .files-pane__empty-hint {
  color: var(--files-pane-accent);
}

.files-pane__empty:not(:disabled):hover svg {
  opacity: 0.5;
}

.files-pane__empty-title {
  font-size: 14px;
  font-weight: 600;
  color: #73808c;
}

.files-pane__empty-hint {
  font-size: 12px;
}

/* 8px + the grid's own 4px top padding = the 12px it puts between its own rows. */
.files-pane__grid + .files-pane__grid {
  margin-top: 8px;
}
</style>
